import React, { useState, useEffect, useRef } from 'react';
import { Step, Stepper, StepButton, StepContent, Box } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import AssignFinancialStep from './steps/AssignFinancialStep';
import RegisterD365Step from './steps/RegisterD365Step';
import ConsiderReviewMeetingStep from './steps/ConsiderReviewMeetingStep';
import ReviewMeetingStep from './steps/ReviewMeetingStep';
import AssessRelevanceStep from './steps/AssessRelevanceStep';
import LinkDocumentAndLineStep from './steps/LinkDocumentAndLineStep';
import type { IContractToRegistrationProcessActivity } from '../../classes/ContractToRegistrationProcessResponse';
import { Activities, RegistrationStatuses } from '../../services/rtkApi';
import ControlStep from './steps/ControlStep';

import ControlSuperofficeNumber from './steps/ControlSuperofficeNumber';
import { FormContainerKontraktsforvaltning } from '@klp/formgenerator/index';
import { getActivityIsDisabled } from '@klp/contractregistration/utils';
import { useCurrentUser } from '@klp/auth';
import { ContractFormAlert } from '../ContractFormAlert';

type StepperProps = {
    activities: Array<IContractToRegistrationProcessActivity>;
    registreringStatus?: string;
    skalRegistreres: boolean;
    prosessInstansId: string;
};
export const VerticalStepper = ({
    activities,
    registreringStatus,
    skalRegistreres,
    prosessInstansId,
}: StepperProps) => {
    const { t } = useTranslation();
    const [activeSteps, setActiveSteps] = useState<Array<number>>([]);
    const { currentUser } = useCurrentUser();
    const stepRefs = useRef<Array<HTMLDivElement | null>>([]);

    const activitiesMap = activities.reduce(
        (acc, aktiviteter) => ({
            ...acc,
            [aktiviteter.taskDefinitionId]: aktiviteter,
        }),
        {} as {
            [key in keyof typeof Activities]: IContractToRegistrationProcessActivity;
        },
    );

    useEffect(() => {
        const activeStepIndexes = activities.reduce<Array<number>>(
            (acc, activity, index) => {
                if (activity.id !== null && activity.utfortDato === null) {
                    acc.push(index);
                }
                return acc;
            },
            [],
        );

        setActiveSteps(
            activeStepIndexes.length > 0
                ? activeStepIndexes
                : [activities.length - 1],
        );
    }, [activities]);

    const handleStepClick = (stepIndex: number) => () => {
        if (activeSteps.includes(stepIndex)) {
            setActiveSteps([...activeSteps.filter((s) => s !== stepIndex)]);
        } else {
            setActiveSteps([...activeSteps, stepIndex]);
        }
    };

    const handleComplete = () => {};

    const isCompletedStep = (
        activity: IContractToRegistrationProcessActivity,
    ) => activity.id !== null && activity.utfortDato !== null;

    const isActiveStep = (index: number) => activeSteps.includes(index);

    const getStepContent = (
        activity: IContractToRegistrationProcessActivity,
    ) => {
        switch (activity.taskDefinitionId) {
            case Activities.VURDER_RELEVANS:
                return (
                    <AssessRelevanceStep
                        handleCompleteStep={handleComplete}
                        prosessInstansId={prosessInstansId}
                        activity={activity}
                    />
                );
            case Activities.TILDEL_OKONOMI:
                return (
                    <AssignFinancialStep
                        status={RegistrationStatuses.TILDELT_OKONOMI}
                        activity={activity}
                        prosessInstansId={prosessInstansId}
                        handleCompleteStep={handleComplete}
                    />
                );
            case Activities.UTFOR_REGISTRERING_D365:
                return (
                    <RegisterD365Step
                        status={{
                            [RegistrationStatuses.REGISTRERING_D365]:
                                RegistrationStatuses.REGISTRERING_D365,
                            [RegistrationStatuses.TIL_QA]:
                                RegistrationStatuses.TIL_QA,
                        }}
                        activity={activity}
                        handleCompleteStep={handleComplete}
                        prosessInstansId={prosessInstansId}
                    />
                );
            case Activities.KONTROLLER_D365_REGISTRERING:
                return (
                    <ControlStep
                        status={{
                            [RegistrationStatuses.QA_UTFORT]:
                                RegistrationStatuses.QA_UTFORT,
                        }}
                        activity={{
                            [Activities.KONTROLLER_D365_REGISTRERING]:
                                activitiesMap?.[
                                    Activities.KONTROLLER_D365_REGISTRERING
                                ],
                            [Activities.UTFOR_REGISTRERING_D365]:
                                activitiesMap?.[
                                    Activities.UTFOR_REGISTRERING_D365
                                ],
                        }}
                        handleCompleteStep={handleComplete}
                        prosessInstansId={prosessInstansId}
                    />
                );
            case Activities.GODKJENNING_AV_KONTRAKT:
                return (
                    <ConsiderReviewMeetingStep
                        status={{
                            [RegistrationStatuses.TIL_GODKJENNING]:
                                RegistrationStatuses.TIL_GODKJENNING,
                            [RegistrationStatuses.GODKJENT]:
                                RegistrationStatuses.GODKJENT,
                        }}
                        activity={activity}
                        handleCompleteStep={handleComplete}
                        prosessInstansId={prosessInstansId}
                    />
                );
            case Activities.GJENNOMGANGSMOTE:
                return (
                    <ReviewMeetingStep
                        status={RegistrationStatuses.GODKJENT}
                        activity={activity}
                        handleCompleteStep={handleComplete}
                        prosessInstansId={prosessInstansId}
                    />
                );
            case Activities.OPPDATER_KUNDENUMMER_I_SUPER_OFFICE:
                return (
                    <ControlSuperofficeNumber
                        status={RegistrationStatuses.GODKJENT}
                        activity={activity}
                        handleCompleteStep={handleComplete}
                        prosessInstansId={prosessInstansId}
                    />
                );
            case Activities.TILKNYTT_DOKUMENT_TIL_KONTRAKT:
                return (
                    <LinkDocumentAndLineStep
                        handleCompleteStep={handleComplete}
                        status={RegistrationStatuses.FULLFORT}
                        activity={{
                            [Activities.TILKNYTT_DOKUMENT_TIL_KONTRAKT]:
                                activitiesMap?.[
                                    Activities.TILKNYTT_DOKUMENT_TIL_KONTRAKT
                                ],
                            [Activities.VURDER_RELEVANS]:
                                activitiesMap?.[Activities.VURDER_RELEVANS],
                        }}
                        prosessInstansId={prosessInstansId}
                    />
                );
            default:
                return (
                    <FormContainerKontraktsforvaltning
                        activity={activity}
                        prosessInstansId={prosessInstansId}
                        isDisabled={getActivityIsDisabled(
                            activity,
                            currentUser,
                        )}
                        disableExplicitSave={false}
                    />
                );
        }
    };

    return (
        <Box>
            <Stepper nonLinear={true} orientation="vertical">
                {!skalRegistreres &&
                registreringStatus !== RegistrationStatuses.FULLFORT &&
                activities[0] ? (
                    <Step
                        key={`checkOffContract.step.${activities[0].taskDefinitionId}.title`}
                        completed={isCompletedStep(activities[0])}
                        active={true}
                    >
                        <StepButton
                            color="inherit"
                            onClick={handleStepClick(0)}
                        >
                            {t(
                                `checkOffContract.step.${activities[0].taskDefinitionId}.title`,
                            )}
                        </StepButton>
                        <StepContent>
                            {getStepContent(activities[0])}
                        </StepContent>
                    </Step>
                ) : (
                    activities.map((activity, index) => {
                        return (
                            <Step
                                key={activity.taskDefinitionId}
                                active={isActiveStep(index)}
                                completed={isCompletedStep(activity)}
                                ref={(el: HTMLDivElement | null) =>
                                    (stepRefs.current[index] = el)
                                }
                            >
                                <StepButton
                                    color="inherit"
                                    onClick={handleStepClick(index)}
                                >
                                    {t(
                                        Object.values(Activities).includes(
                                            activity.taskDefinitionId as Activities,
                                        )
                                            ? `checkOffContract.step.${activity.taskDefinitionId}.title`
                                            : activity.navn,
                                    )}
                                </StepButton>
                                <StepContent>
                                    <ContractFormAlert
                                        activity={activity}
                                        currentUser={currentUser}
                                    />

                                    {getStepContent(activity)}
                                </StepContent>
                            </Step>
                        );
                    })
                )}
            </Stepper>
        </Box>
    );
};
